import { api } from "./api";

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  } catch (e) {
    return null;
  }
}

export async function pushStatus() {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return sub ? "enabled" : "disabled";
}

const BINDING_KEY = "push_binding";

function sameKey(sub, keyBytes) {
  const current = sub?.options?.applicationServerKey;
  if (!current) return false;
  const a = new Uint8Array(current);
  return a.length === keyBytes.length && a.every((b, i) => b === keyBytes[i]);
}

function bindingFor(reg, publicKey) {
  const sw = reg.active || reg.waiting || reg.installing;
  return `${window.location.origin}|${sw?.scriptURL || ""}|${publicKey}`;
}

async function freshSubscribe(reg, keyBytes, stale) {
  if (stale) {
    try { await api.post("/push/unsubscribe", { subscription: stale.toJSON() }); } catch (e) { console.debug("stale unsubscribe failed", e); }
    try { await stale.unsubscribe(); } catch (e) { console.debug("stale sub.unsubscribe failed", e); }
  }
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
}

// Ensures the subscription is bound to the current VAPID key, origin and service worker.
async function syncSubscription(reg, publicKey) {
  const keyBytes = urlBase64ToUint8Array(publicKey);
  const binding = bindingFor(reg, publicKey);
  let sub = await reg.pushManager.getSubscription();
  const stale = sub && (!sameKey(sub, keyBytes) || localStorage.getItem(BINDING_KEY) !== binding);
  if (!sub || stale) {
    try {
      sub = await freshSubscribe(reg, keyBytes, stale ? sub : null);
    } catch (e) {
      const leftover = await reg.pushManager.getSubscription();
      sub = await freshSubscribe(reg, keyBytes, leftover);
    }
  }
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  localStorage.setItem(BINDING_KEY, binding);
  return sub;
}

async function fetchPublicKey() {
  const { data } = await api.get("/push/vapid-public-key");
  return data.public_key;
}

export class PushError extends Error {
  constructor(code, message) { super(message); this.code = code; }
}

// Throws PushError with code: unsupported | insecure | denied | dismissed | not_configured | subscribe_failed
export async function enablePush() {
  if (!pushSupported()) throw new PushError("unsupported", "This browser can't howl with push alerts.");
  if (!window.isSecureContext) throw new PushError("insecure", "Push alerts need a secure (HTTPS) connection.");
  const permission = await Notification.requestPermission();
  if (permission === "denied") throw new PushError("denied", "Notification permission was denied.");
  if (permission !== "granted") throw new PushError("dismissed", "The permission prompt was closed without a choice.");
  try {
    const reg = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
    await navigator.serviceWorker.ready;
    const publicKey = await fetchPublicKey();
    if (!publicKey) throw new PushError("not_configured", "Push is not configured on the server.");
    await syncSubscription(reg, publicKey);
  } catch (e) {
    if (e instanceof PushError) throw e;
    const detail = e?.response?.data?.detail || e?.message || "unknown error";
    throw new PushError("subscribe_failed", `Couldn't register this device for alerts (${detail}).`);
  }
  window.dispatchEvent(new CustomEvent("pack:push-changed", { detail: { enabled: true } }));
  return true;
}

// Silently restore push for a wolf who already granted permission before — no prompt, no click.
export async function autoResubscribe() {
  if (!pushSupported() || !window.isSecureContext || Notification.permission !== "granted") return false;
  try {
    const reg = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
    if (!reg) return false;
    await navigator.serviceWorker.ready;
    const publicKey = await fetchPublicKey();
    if (!publicKey) return false;
    await syncSubscription(reg, publicKey);
    return true;
  } catch (e) {
    return false;
  }
}

// A new service worker taking control means the old subscription may be gone — rebind it.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    localStorage.removeItem(BINDING_KEY);
    autoResubscribe();
  });
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return true;
  const sub = await reg.pushManager.getSubscription();
  localStorage.removeItem(BINDING_KEY);
  if (sub) {
    try { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); } catch (e) { console.debug("push unsubscribe failed", e); }
    await sub.unsubscribe();
  }
  return true;
}
